// Sizes held for customers, shown in place of the style grid: a form for a new
// hold (when a size was picked in the size picker's Hold mode) and this store's
// open holds. Online only — holds are shared by every till in the store.
import React, { useState } from "react";
import dayjs from "dayjs";
import { defaultHeldUntil, MAX_HOLD_DAYS, searchHolds, validateHoldDraft } from "../../../utils/fashion/holds";
import { formatMoney } from "../../../utils/fashion/money";
import { basePrice } from "../../../utils/fashion/markdowns";
import { readVariantAxes } from "../../../utils/fashion/styleIndex";
import { localStockLabel } from "../../../utils/fashion/storeStock";
import EmptyState from "./EmptyState";
import "./FashionWorkPanels.css";

const showUntil = (d) => (d && dayjs(d).isValid() ? dayjs(d).format("DD MMM, HH:mm") : d || "");

// Status names the store floor uses (v4 board). The server only knows HLD/EXP;
// "Expires today" is an open hold whose time runs out before midnight.
export function holdState(hold, now = dayjs()) {
  if (hold?.status === "EXP") return { key: "EXP", label: "Expired", tone: "bad" };
  const until = hold?.heldUntil ? dayjs(hold.heldUntil) : null;
  if (until?.isValid() && until.isSame(now, "day")) return { key: "TODAY", label: "Expires today", tone: "warn" };
  return { key: "HLD", label: "On hold", tone: "info" };
}

export const HOLD_FILTERS = [
  { key: "ACTIVE", label: "Active", keep: (h) => h.status !== "EXP" },
  { key: "EXP", label: "Expired", keep: (h) => h.status === "EXP" },
  { key: "ALL", label: "All", keep: () => true },
];

// Held value uses the till's own price for the item; holds whose item isn't on
// the till count toward pieces but not value.
export function holdStats(holds, productById, now = dayjs()) {
  const list = holds || [];
  const active = list.filter((h) => h.status !== "EXP");
  const priceOf = (h) => basePrice(productById?.(h.productId));
  return {
    active: active.length,
    pieces: active.reduce((n, h) => n + h.qty, 0),
    today: active.filter((h) => holdState(h, now).key === "TODAY").length,
    expired: list.filter((h) => h.status === "EXP").length,
    value: active.reduce((n, h) => n + priceOf(h) * h.qty, 0),
  };
}

export function variantLabel(product) {
  if (!product) return "";
  const { size, colour } = readVariantAxes(product);
  return [product.name, [colour, size].filter(Boolean).join(" / ")].filter(Boolean).join(" · ");
}

function HoldForm({ product, customer, namedCustomer, alreadyHeld, create, onDone }) {
  const [draft, setDraft] = useState(() => ({
    qty: "1",
    customerName: namedCustomer ? customer?.name || "" : "",
    mobileNo: namedCustomer ? customer?.mobileNo || "" : "",
    heldUntil: defaultHeldUntil(),
    note: "",
  }));
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setDraft((d) => ({ ...d, [field]: e.target.value }));
  const stock = localStockLabel(product);
  const latest = dayjs().add(MAX_HOLD_DAYS, "day").format("YYYY-MM-DDTHH:mm");

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    const { hold, error } = validateHoldDraft({ ...draft, productId: product.mProductId, customerId: namedCustomer ? customer?.cwrCustomerId : null });
    if (error) {
      setMsg(error);
      return;
    }
    setSaving(true);
    setMsg("");
    try {
      const res = await create(hold);
      if (res.ok) onDone(`Held ${hold.qty} × ${variantLabel(product)}${res.documentNo ? ` (${res.documentNo})` : ""} until ${showUntil(hold.heldUntil)}.`);
      else setMsg(res.message);
    } catch {
      setMsg("Couldn't save the hold. Check the connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="fp-hold-form" onSubmit={save} aria-label="New hold">
      <p className="fp-hold-item">
        <span className="fp-panel-doc">{variantLabel(product)}</span>
        <span className="fp-muted">{[stock, alreadyHeld ? `${alreadyHeld} already held` : ""].filter(Boolean).join(" · ")}</span>
      </p>
      <div className="fp-hold-fields">
        <label className="fp-field">
          <span className="fp-lbl fp-muted">Customer name</span>
          <input className="fp-input" value={draft.customerName} onChange={set("customerName")} autoComplete="off" />
        </label>
        <label className="fp-field">
          <span className="fp-lbl fp-muted">Mobile</span>
          <input className="fp-input fp-num" value={draft.mobileNo} onChange={set("mobileNo")} inputMode="tel" autoComplete="off" />
        </label>
        <label className="fp-field fp-field-sm">
          <span className="fp-lbl fp-muted">Pieces</span>
          <input className="fp-input fp-num" value={draft.qty} onChange={set("qty")} inputMode="numeric" />
        </label>
        <label className="fp-field">
          <span className="fp-lbl fp-muted">Hold until</span>
          <input className="fp-input fp-num" type="datetime-local" value={draft.heldUntil} max={latest} onChange={set("heldUntil")} />
        </label>
        <label className="fp-field fp-field-wide">
          <span className="fp-lbl fp-muted">Note</span>
          <input className="fp-input" value={draft.note} onChange={set("note")} placeholder="e.g. coming back after work" autoComplete="off" />
        </label>
      </div>
      {msg && (
        <p className="fp-line-msg is-err" role="alert">
          {msg}
        </p>
      )}
      <div className="fp-row">
        <button type="submit" className="fp-btn-primary" disabled={saving}>
          {saving ? "Saving…" : "Hold this size"}
        </button>
        <button type="button" className="fp-btn-ghost" onClick={() => onDone("")} disabled={saving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function FashionHoldsPanel({ holdsApi, draftProduct, customer, namedCustomer, productById, canAddToBill, onAddToBill, onDraftDone, onClose, backLabel = "Back to styles", tillData }) {
  const { holds, status, online, reload, create, release } = holdsApi;
  const [term, setTerm] = useState("");
  const [msg, setMsg] = useState("");
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("ACTIVE");
  const keep = (HOLD_FILTERS.find((f) => f.key === filter) || HOLD_FILTERS[0]).keep;
  const rows = searchHolds(holds, term).filter(keep);
  const stats = holdStats(holds, productById);

  const doRelease = async (hold) => {
    if (confirming !== hold.id) {
      setConfirming(hold.id);
      return;
    }
    setConfirming(null);
    setBusy(hold.id);
    setMsg("");
    try {
      const res = await release(hold.id);
      setMsg(res.ok ? `Released ${hold.documentNo || "the hold"}.` : res.message);
    } catch {
      setMsg("Couldn't release the hold. Check the connection and try again.");
    } finally {
      setBusy(null);
    }
  };

  const addToBill = (hold) => {
    const product = productById(hold.productId);
    if (!product) {
      setMsg("This item isn't on the till. Run a sync, then try again.");
      return;
    }
    setMsg(onAddToBill(hold, product) || "");
  };

  return (
    <section className="fp-panel" aria-label="Holds">
      <div className="fp-panel-head">
        <h2 className="fp-panel-title">{draftProduct ? "Hold a size" : "Holds"}</h2>
        <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={onClose}>
          {backLabel}
        </button>
      </div>
      {!online ? (
        <EmptyState icon="offline" title="You're offline" hint="Holds need a connection, because every till in the store shares them. Reconnect the till and try again." />
      ) : (
        <>
          {draftProduct && (
            <HoldForm
              key={draftProduct.mProductId}
              product={draftProduct}
              customer={customer}
              namedCustomer={namedCustomer}
              alreadyHeld={holds.filter((h) => h.productId === draftProduct.mProductId && h.status === "HLD").reduce((n, h) => n + h.qty, 0)}
              create={create}
              onDone={(text) => {
                setMsg(text);
                onDraftDone();
              }}
            />
          )}
          {msg && (
            <p className="fp-line-msg" role="status">
              {msg}
            </p>
          )}
          <div className="fp-wp-stats" aria-label="Holds summary">
            <div>
              <span>On hold</span>
              <b className="fp-num">{stats.active}</b>
              <small className="fp-num">{stats.pieces === 1 ? "1 piece" : `${stats.pieces} pieces`}</small>
            </div>
            <div className={stats.today ? "is-warn" : ""}>
              <span>Expires today</span>
              <b className="fp-num">{stats.today}</b>
            </div>
            <div className={stats.expired ? "is-bad" : ""}>
              <span>Expired, still on the shelf</span>
              <b className="fp-num">{stats.expired}</b>
            </div>
            <div>
              <span>Held value</span>
              <b className="fp-num">{formatMoney(stats.value, tillData)}</b>
            </div>
          </div>
          <div className="fp-row">
            <div className="fp-wp-seg" role="group" aria-label="Show holds">
              {HOLD_FILTERS.map((f) => (
                <button key={f.key} type="button" className={filter === f.key ? "is-on" : ""} aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>
                  {f.label}
                </button>
              ))}
            </div>
            <input className="fp-input fp-panel-search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search hold no., item, name or mobile" aria-label="Search holds" />
            <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => reload()} disabled={status === "loading"}>
              Refresh
            </button>
          </div>
          {status === "error" && (
            <p className="fp-line-msg is-err" role="alert">
              Couldn't load holds. The list below may be out of date.
            </p>
          )}
          <ul className="fp-panel-list" aria-busy={status === "loading" ? "true" : "false"}>
            {rows.length === 0 && (
              status === "loading" ? (
                <li className="fp-empty">Loading holds…</li>
              ) : term ? (
                <EmptyState as="li" icon="search" title={`No hold matches “${term}”.`} hint="Check the spelling, or try another search." />
              ) : holds.length && filter !== "ALL" ? (
                <EmptyState as="li" title={filter === "EXP" ? "No expired holds." : "No active holds."} hint="Pick All to see every hold in the store." />
              ) : (
                <EmptyState as="li" title="No sizes are held in this store." hint="Holds made at any till in the store show here." />
              )
            )}
            {rows.map((hold) => {
              const expired = hold.status === "EXP";
              const state = holdState(hold);
              return (
                <li key={hold.id} className="fp-panel-row">
                  <span className="fp-panel-main">
                    <span className="fp-panel-doc">
                      <span className="fp-num">{hold.documentNo}</span> {hold.productName}
                      {hold.qty > 1 && <span className="fp-muted fp-num"> ×{hold.qty}</span>}
                      <span className={`fp-wp-pill is-${state.tone}`}>{state.label}</span>
                    </span>
                    <span className="fp-muted">
                      {[hold.customerName, hold.mobileNo].filter(Boolean).join(" · ")} · held until {showUntil(hold.heldUntil)}
                      {hold.note ? ` · ${hold.note}` : ""}
                    </span>
                  </span>
                  <span className="fp-panel-acts">
                    {!expired && (
                      <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={busy === hold.id} onClick={() => doRelease(hold)} onBlur={() => setConfirming((c) => (c === hold.id ? null : c))}>
                        {busy === hold.id ? "Releasing…" : confirming === hold.id ? "Confirm release" : "Release to shelf"}
                      </button>
                    )}
                    <button type="button" className="fp-btn-ghost fp-btn-sm" disabled={!canAddToBill} title={canAddToBill ? "" : "Finish the return first"} onClick={() => addToBill(hold)}>
                      Add to bill
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
